import { defineStore } from 'pinia';
import { ref } from 'vue';
import type {
  IAssessmentWithDetails,
  IChildAssessmentsResponse,
  IResetAssessmentsResponse,
  ISectionConfigurationResponse,
  IUpdateAptitudeSectionConfigurationRequest,
} from '@manage/shared-types';
import { apiClient } from '@/services/api';
import { extractErrorMessage } from '@/utils/apiError';

export const useAssessmentsStore = defineStore('assessments', () => {
  // Map of userId -> assessments for that user
  const assessmentsByUser = ref<Map<number, IAssessmentWithDetails[]>>(new Map());
  const currentAssessment = ref<IAssessmentWithDetails | null>(null);
  const loadingUsers = ref<Set<number>>(new Set());
  const errorsByUser = ref<Map<number, string>>(new Map());

  // Reset modal state
  const resetModalData = ref<IChildAssessmentsResponse | null>(null);
  const resetModalLoading = ref(false);
  const resetModalError = ref<string>('');
  const resetInProgress = ref(false);

  // Single assessment loading state (for detail view)
  const loadingAssessment = ref(false);
  const assessmentError = ref<string>('');

  const isLoading = (userId: number): boolean => {
    return loadingUsers.value.has(userId);
  };

  const getError = (userId: number): string => {
    return errorsByUser.value.get(userId) || '';
  };

  const getAssessments = (userId: number): IAssessmentWithDetails[] => {
    return assessmentsByUser.value.get(userId) || [];
  };

  const fetchUserAssessments = async (userId: number): Promise<void> => {
    loadingUsers.value.add(userId);
    errorsByUser.value.delete(userId);

    try {
      const response = await apiClient.get<IAssessmentWithDetails[]>(`/assessments/user/${userId}`);

      assessmentsByUser.value.set(userId, response.data);
    } catch (err) {
      const errorMessage = extractErrorMessage(err, 'An unexpected error occurred');

      errorsByUser.value.set(userId, errorMessage);
      console.error('Error fetching user assessments:', err);
      throw err;
    } finally {
      loadingUsers.value.delete(userId);
    }
  };

  const fetchAssessment = async (assessmentId: number): Promise<void> => {
    loadingAssessment.value = true;
    assessmentError.value = '';

    try {
      const response = await apiClient.get<IAssessmentWithDetails>(`/assessments/${assessmentId}`);

      currentAssessment.value = response.data;
    } catch (err) {
      const errorMessage = extractErrorMessage(err, 'An unexpected error occurred');

      assessmentError.value = errorMessage;
      console.error('Error fetching assessment:', err);
      throw err;
    } finally {
      loadingAssessment.value = false;
    }
  };

  const clearCurrentAssessment = (): void => {
    currentAssessment.value = null;
  };

  const clearAssessments = (): void => {
    assessmentsByUser.value.clear();
    errorsByUser.value.clear();
  };

  const clearUserAssessments = (userId: number): void => {
    assessmentsByUser.value.delete(userId);
    errorsByUser.value.delete(userId);
  };

  const fetchChildAssessmentsForReset = async (
    parentAssessmentId: number
  ): Promise<IChildAssessmentsResponse> => {
    resetModalLoading.value = true;
    resetModalError.value = '';
    resetModalData.value = null;

    try {
      const response = await apiClient.get<IChildAssessmentsResponse>(
        `/assessments/${parentAssessmentId}/children`
      );

      resetModalData.value = response.data;

      return response.data;
    } catch (err) {
      const errorMessage = extractErrorMessage(err, 'Failed to load child assessments');

      resetModalError.value = errorMessage;
      console.error('Error fetching child assessments for reset:', err);
      throw err;
    } finally {
      resetModalLoading.value = false;
    }
  };

  const resetChildAssessments = async (
    parentAssessmentId: number,
    childAssessmentIds: number[]
  ): Promise<IResetAssessmentsResponse> => {
    resetInProgress.value = true;
    resetModalError.value = '';

    try {
      const response = await apiClient.post<IResetAssessmentsResponse>('/assessments/reset', {
        parentAssessmentId,
        childAssessmentIds,
      });

      return response.data;
    } catch (err) {
      const errorMessage = extractErrorMessage(err, 'Failed to reset assessments');

      resetModalError.value = errorMessage;
      console.error('Error resetting assessments:', err);
      throw err;
    } finally {
      resetInProgress.value = false;
    }
  };

  const clearResetModalState = (): void => {
    resetModalData.value = null;
    resetModalLoading.value = false;
    resetModalError.value = '';
    resetInProgress.value = false;
  };

  // Accommodation Settings API Methods
  // Note: Modal manages its own local state for UI responsiveness
  const fetchAccommodationSettings = async (
    parentAssessmentId: number
  ): Promise<ISectionConfigurationResponse> => {
    try {
      const response = await apiClient.get<ISectionConfigurationResponse>(
        `/aptitude-section-configurations/assessment/${parentAssessmentId}`
      );

      return response.data;
    } catch (err) {
      const errorMessage = extractErrorMessage(err, 'Failed to load accommodation settings');

      console.error('Error fetching accommodation settings:', err);
      throw new Error(errorMessage);
    }
  };

  const updateAccommodationSectionStatus = async (
    sectionId: number,
    enabled: boolean
  ): Promise<void> => {
    try {
      await apiClient.patch(`/aptitude-section-configurations/${sectionId}/status`, {
        enabled,
      });
    } catch (err) {
      console.error('Error updating accommodation section status:', err);
      throw err;
    }
  };

  const updateAccommodationSection = async (
    sectionId: number,
    data: IUpdateAptitudeSectionConfigurationRequest
  ): Promise<void> => {
    try {
      await apiClient.patch(`/aptitude-section-configurations/${sectionId}`, data);
    } catch (err) {
      console.error('Error updating accommodation section:', err);
      throw err;
    }
  };

  const resetAccommodationSettings = async (
    parentAssessmentId: number
  ): Promise<ISectionConfigurationResponse> => {
    try {
      const response = await apiClient.post<ISectionConfigurationResponse>(
        `/aptitude-section-configurations/assessment/${parentAssessmentId}/reset`
      );

      return response.data;
    } catch (err) {
      const errorMessage = extractErrorMessage(err, 'Failed to reset accommodation settings');

      console.error('Error resetting accommodation settings:', err);
      throw new Error(errorMessage);
    }
  };

  return {
    assessmentsByUser,
    currentAssessment,
    loadingUsers,
    errorsByUser,
    loadingAssessment,
    assessmentError,
    resetModalData,
    resetModalLoading,
    resetModalError,
    resetInProgress,
    isLoading,
    getError,
    getAssessments,
    fetchUserAssessments,
    fetchAssessment,
    clearCurrentAssessment,
    clearAssessments,
    clearUserAssessments,
    fetchChildAssessmentsForReset,
    resetChildAssessments,
    clearResetModalState,
    // Accommodation Settings
    fetchAccommodationSettings,
    updateAccommodationSectionStatus,
    updateAccommodationSection,
    resetAccommodationSettings,
  };
});
